import React, { useEffect, useMemo, useRef, useState } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { FieldRenderer } from "@/components/FieldRenderer";
import { useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Loader2, Save, FileDown, ChevronLeft, ChevronRight, CheckCircle2, Circle, Zap, ShieldCheck,
  Plus, Users, Baby, Trash2, ListChecks,
} from "lucide-react";

function isFilled(v) {
  if (v == null || v === "") return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "object") return Boolean(v.data || v.url || v.path);
  return true;
}

function photoSlots(key, label) {
  const s = `${key || ""} ${label || ""}`.toLowerCase();
  let cat = null;
  if (s.includes("insur")) cat = "insurance";
  else if (["driver", "license", "identification", "photo id", "id card", "id-card", "id_card", "govid", "gov id", "state id"].some((t) => s.includes(t)) || ["id", "idPhoto", "id_photo"].includes(key)) cat = "id";
  if (!cat) return [];
  const out = [];
  if (s.includes("back")) out.push(`_${cat}_photo_back`);
  else if (s.includes("front")) out.push(`_${cat}_photo_front`);
  out.push(`_${cat}_photo`);
  return out;
}

function profileValueFor(f, pf) {
  if (pf[f.key] !== undefined) return pf[f.key];
  if (f.type === "photo") {
    for (const slot of photoSlots(f.key, f.label)) {
      if (pf[slot] !== undefined) return pf[slot];
    }
  }
  return undefined;
}

// Pediatric "multi-child" template markers.
const CHILD_SECTION_KEY = "patientInfo";
const CHILD_LABEL_FIELD = "childName";

function hasChildSectionFor(steps) {
  return (steps || []).some((s) => s.key === CHILD_SECTION_KEY && (s.fields || []).some((f) => f.key === CHILD_LABEL_FIELD));
}

export default function Dashboard() {
  const { user } = useAuth();
  const [steps, setSteps] = useState([]);
  const [accent, setAccent] = useState("#0F52BA");
  const [clinicName, setClinicName] = useState("");
  const [clinicLogo, setClinicLogo] = useState(null);
  const [data, setData] = useState({});
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("draft");
  const [portalActive, setPortalActive] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const [profileFields, setProfileFields] = useState({});
  const [esignOpen, setEsignOpen] = useState(false);
  const [esignUrl, setEsignUrl] = useState("");
  const [esignBusy, setEsignBusy] = useState(false);
  const [blitzes, setBlitzes] = useState([]);
  const [formId, setFormId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newChildName, setNewChildName] = useState("");
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const pollRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeClinic = searchParams.get("clinic") || "";
  const clinicParam = activeClinic ? { clinic_id: activeClinic } : {};
  const formParams = { ...(activeClinic ? { clinic_id: activeClinic } : {}), ...(formId ? { form_id: formId } : {}) };

  const hasChildSection = useMemo(
    () => steps.some((s) => s.key === CHILD_SECTION_KEY && (s.fields || []).some((f) => f.key === CHILD_LABEL_FIELD)),
    [steps]
  );

  const stepVisible = (s) => {
    const cond = s.visibleIf;
    if (!cond || !cond.field) return true;
    for (const sec of Object.values(data || {})) {
      if (sec && typeof sec === "object" && sec[cond.field] !== undefined) {
        return String(sec[cond.field]) === String(cond.equals);
      }
    }
    return false;
  };
  const visibleSteps = useMemo(() => steps.filter(stepVisible), [steps, data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (step > visibleSteps.length - 1) setStep(Math.max(0, visibleSteps.length - 1));
  }, [visibleSteps.length, step]);

  const getMissing = () => {
    const out = [];
    visibleSteps.forEach((s, si) => {
      (s.fields || []).forEach((f) => {
        if (f.type === "signature") return;
        if (f.required && !isFilled((data[s.key] || {})[f.key])) out.push({ stepIndex: si, key: f.key, label: f.label });
      });
    });
    return out;
  };

  const applyLoaded = (res, tplData, pf) => {
    const loaded = res.data.data || {};
    if (!hasChildSectionFor(tplData.steps)) {
      const pi = loaded.patientInfo || {};
      loaded.patientInfo = { ...pi, patientName: pi.patientName || user?.name || "", email: pi.email || user?.email || "" };
    }
    setStatus(res.data.status || "draft");
    const meaningful = Object.entries(loaded).some(([sk, sec]) =>
      sec && typeof sec === "object" && Object.entries(sec).some(([fk, v]) => !["patientName", "email"].includes(fk) && isFilled(v)));
    // Blitz: always carry shared saved details into ANY empty field (never overwrite what's
    // already there, never touch signatures). Child-specific fields are excluded from the
    // profile server-side, so they never leak between children.
    let count = 0;
    const next = { ...loaded };
    if (pf && Object.keys(pf).length) {
      (tplData.steps || []).forEach((s) => {
        const sec = { ...(next[s.key] || {}) };
        s.fields.forEach((f) => {
          if (f.type === "signature") return;
          if (isFilled(sec[f.key])) return;
          const val = profileValueFor(f, pf);
          if (val !== undefined) { sec[f.key] = val; count++; }
        });
        next[s.key] = sec;
      });
    }
    setData(next);
    if (count && !meaningful) setTimeout(() => toast.success(`⚡ Blitz Autofill added ${count} of your saved details.`), 300);
  };

  const loadForm = async (fid, pfOverride) => {
    const params = { ...(activeClinic ? { clinic_id: activeClinic } : {}), ...(fid ? { form_id: fid } : {}) };
    const res = await api.get("/forms/me", { params });
    applyLoaded(res, { steps }, pfOverride !== undefined ? pfOverride : profileFields);
    setStep(0);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setStep(0);
      try {
        const [tpl, listRes] = await Promise.all([
          api.get("/forms/template", { params: clinicParam }),
          api.get("/forms/me/list", { params: clinicParam }),
        ]);
        setSteps(tpl.data.steps || []);
        if (tpl.data.accent_color) setAccent(tpl.data.accent_color);
        if (tpl.data.clinic_name) setClinicName(tpl.data.clinic_name);
        setClinicLogo(tpl.data.has_logo ? `${API}/clinic/logo/${tpl.data.clinic_id}` : null);
        setPortalActive(tpl.data.portal_active !== false);
        let pf = {};
        try { pf = (await api.get("/profile/me")).data.fields || {}; setProfileFields(pf); }
        catch (e) { console.debug("No saved profile yet:", e?.response?.status); }

        const list = listRes.data || [];
        setBlitzes(list);
        const urlForm = searchParams.get("form");
        const chosen = (urlForm && list.some((b) => b.form_id === urlForm)) ? urlForm : (list[0]?.form_id || null);
        setFormId(chosen);
        const res = await api.get("/forms/me", { params: { ...clinicParam, ...(chosen ? { form_id: chosen } : {}) } });
        applyLoaded(res, tpl.data, pf);
      } catch (e) {
        toast.error("Could not load your paperwork.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClinic]);

  const refreshBlitzes = async () => {
    try {
      const r = await api.get("/forms/me/list", { params: clinicParam });
      setBlitzes(r.data || []);
      return r.data || [];
    } catch { return blitzes; }
  };

  const switchBlitz = async (fid) => {
    if (fid === formId) return;
    setLoading(true);
    try {
      // Save the current form first so its shared edits reach the profile, then blitz them
      // into the target form (fill empty like-fields across all children & companies).
      let pf = profileFields;
      if (formId && status !== "submitted") {
        try {
          await api.put("/forms/me", { data, status: "draft", clinic_id: activeClinic || undefined, form_id: formId });
          pf = (await api.get("/profile/me")).data.fields || pf;
          setProfileFields(pf);
        } catch (e) { console.debug("pre-switch save failed:", e?.response?.status); toast.warning("Couldn't sync your latest edits before switching — some details may not carry over."); }
      }
      setFormId(fid);
      const next = new URLSearchParams(searchParams);
      if (fid) next.set("form", fid); else next.delete("form");
      setSearchParams(next, { replace: true });
      await loadForm(fid, pf);
      refreshBlitzes();
    } finally {
      setLoading(false);
    }
  };

  const applyProfileToShared = (base) => {
    const pf = profileFields || {};
    if (!Object.keys(pf).length) return base;
    const next = { ...base };
    (steps || []).forEach((s) => {
      if (hasChildSection && s.key === CHILD_SECTION_KEY) return; // keep child section untouched
      const sec = { ...(next[s.key] || {}) };
      s.fields.forEach((f) => {
        if (f.type === "signature") return;
        if (isFilled(sec[f.key])) return;
        const val = profileValueFor(f, pf);
        if (val !== undefined) sec[f.key] = val;
      });
      next[s.key] = sec;
    });
    return next;
  };

  const createBlitz = async ({ label, resetChild }) => {
    setCreating(true);
    try {
      // Persist the current form first so its parent/insurance details are captured in the clone.
      if (resetChild && hasChildSection && formId && status !== "submitted") {
        try { await api.put("/forms/me", { data, status: "draft", clinic_id: activeClinic || undefined, form_id: formId }); } catch (e) { console.debug("pre-save before clone failed:", e?.response?.status); }
      }
      const body = { clinic_id: activeClinic || undefined, label: label || undefined };
      if (resetChild && hasChildSection && formId) {
        body.copy_from = formId;
        body.reset_sections = [CHILD_SECTION_KEY];
        body.label_field = { section: CHILD_SECTION_KEY, key: CHILD_LABEL_FIELD };
      }
      const res = await api.post("/forms/me/new", body);
      const fid = res.data.form_id;
      // Fill any still-empty shared fields (parents, insurance, emergency) from the saved profile.
      let nextData = res.data.data || {};
      if (resetChild && hasChildSection) nextData = applyProfileToShared(nextData);
      setAddOpen(false);
      setNewChildName("");
      setFormId(fid);
      const next = new URLSearchParams(searchParams);
      next.set("form", fid);
      setSearchParams(next, { replace: true });
      setStatus("draft");
      setData(nextData);
      setStep(0);
      // Persist the pre-filled shared data on the new blitz.
      try { await api.put("/forms/me", { data: nextData, status: "draft", clinic_id: activeClinic || undefined, form_id: fid }); } catch (e) { console.debug("persist new blitz failed:", e?.response?.status); }
      await refreshBlitzes();
      toast.success(label ? `New blitz started for ${label}.` : "New blitz started.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start a new blitz.");
    } finally {
      setCreating(false);
    }
  };

  const deleteBlitz = async () => {
    const b = deleteTarget;
    if (!b) return;
    setDeleting(true);
    try {
      await api.delete(`/forms/me/${b.form_id}`, { params: clinicParam });
      const list = await refreshBlitzes();
      if (formId === b.form_id) {
        const nextId = (list[0] && list[0].form_id) || null;
        setFormId(nextId);
        const next = new URLSearchParams(searchParams);
        if (nextId) next.set("form", nextId); else next.delete("form");
        setSearchParams(next, { replace: true });
        setLoading(true);
        try { await loadForm(nextId); } finally { setLoading(false); }
      }
      toast.success("Form deleted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete this form.");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const current = visibleSteps[step] || visibleSteps[0];

  const blitzAutofill = () => {
    const pf = profileFields || {};
    if (!Object.keys(pf).length) {
      toast.info("No saved profile yet — fill in your details and we'll save them for one-click autofill next time.");
      return;
    }
    let count = 0;
    setData((prev) => {
      const next = { ...prev };
      steps.forEach((s) => {
        const sec = { ...(next[s.key] || {}) };
        s.fields.forEach((f) => {
          if (f.type === "signature") return;
          if (isFilled(sec[f.key])) return;
          const val = profileValueFor(f, pf);
          if (val !== undefined) { sec[f.key] = val; count++; }
        });
        next[s.key] = sec;
      });
      return next;
    });
    setTimeout(() => toast.success(count ? `Blitzed ${count} field${count > 1 ? "s" : ""} from your saved profile.` : "Everything's already filled in."), 0);
  };

  const setField = (sectionKey, fieldKey, value) => {
    setData((prev) => ({
      ...prev,
      [sectionKey]: { ...(prev[sectionKey] || {}), [fieldKey]: value },
    }));
  };

  const stepFilledCount = (s) => {
    const section = data[s.key] || {};
    return s.fields.filter((f) => f.type !== "signature" && isFilled(section[f.key])).length;
  };

  const stepFieldTotal = (s) => s.fields.filter((f) => f.type !== "signature").length;

  const progress = useMemo(() => {
    let total = 0;
    let filled = 0;
    visibleSteps.forEach((s) => {
      total += stepFieldTotal(s);
      filled += stepFilledCount(s);
    });
    return total ? Math.round((filled / total) * 100) : 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, visibleSteps]);

  const save = async (newStatus) => {
    if (newStatus === "submitted") {
      const miss = getMissing();
      if (miss.length) {
        setShowErrors(true);
        setStep(miss[0].stepIndex);
        window.scrollTo({ top: 0, behavior: "smooth" });
        toast.error(`Please complete ${miss.length} required field${miss.length > 1 ? "s" : ""} before submitting.`);
        return;
      }
    }
    setSaving(true);
    try {
      const res = await api.put("/forms/me", { data, status: newStatus || status, clinic_id: activeClinic || undefined, form_id: formId || undefined });
      setStatus(res.data.status);
      if (res.data.form_id && res.data.form_id !== formId) setFormId(res.data.form_id);
      refreshBlitzes();
      toast.success(newStatus === "submitted" ? "Paperwork submitted to the office!" : "Progress saved.");
    } catch (e) {
      toast.error("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  useEffect(() => stopPolling, []);

  const pollEsign = () => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get("/forms/me/esign/status", { params: formParams });
        if (r.data.esign_status === "signed" || r.data.status === "submitted") {
          stopPolling();
          setEsignOpen(false);
          setEsignUrl("");
          setStatus("submitted");
          toast.success("Signed! Your paperwork is submitted to the office.");
        }
      } catch (e) { console.debug("esign status poll:", e?.response?.status); }
    }, 4000);
  };

  const startEsign = async () => {
    const miss = getMissing();
    if (miss.length) {
      setShowErrors(true);
      setStep(miss[0].stepIndex);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.error(`Please complete ${miss.length} required field${miss.length > 1 ? "s" : ""} before signing.`);
      return;
    }
    setEsignBusy(true);
    try {
      await api.put("/forms/me", { data, status: "draft", clinic_id: activeClinic || undefined, form_id: formId || undefined });
      const res = await api.post("/forms/me/esign", null, { params: formParams });
      setEsignUrl(res.data.embed_url || res.data.sign_page_url);
      setEsignOpen(true);
      pollEsign();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Could not start the e-signature. Please try again.");
    } finally {
      setEsignBusy(false);
    }
  };

  const downloadPdf = async () => {
    try {
      await save(status); // persist latest before exporting
      const res = await api.get("/forms/me/pdf", { responseType: "blob", params: formParams });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "new-patient-paperwork.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not generate PDF.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <SiteHeader />
        <div className="flex h-[60vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#0F52BA]" />
        </div>
      </div>
    );
  }

  if (!current) {
    return (
      <div className="min-h-screen bg-slate-50">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-20 text-center text-slate-500">
          Your clinic has not published a paperwork form yet. Please check back soon.
        </div>
      </div>
    );
  }

  if (!portalActive) {
    return (
      <div className="min-h-screen bg-slate-50">
        <SiteHeader />
        <div className="mx-auto max-w-lg px-4 py-24 text-center" data-testid="portal-paused">
          <h1 className="text-2xl font-bold text-slate-900">Portal temporarily unavailable</h1>
          <p className="mt-3 text-slate-500">This Nimbus portal is not active right now. Please contact the Nimbus office to complete your paperwork.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteHeader />
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        {/* Top bar */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-3">
              {clinicLogo && (
                <img src={clinicLogo} alt={clinicName} className="h-10 w-auto max-w-[120px] rounded-md object-contain" data-testid="dashboard-clinic-logo" />
              )}
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">New Patient Paperwork</h1>
            </div>
            <p className="mt-1 text-sm text-slate-500">
              Hello{user?.name ? `, ${user.name}` : ""} — complete each section
              {clinicName && <> for <span className="font-bold text-slate-900" data-testid="dashboard-clinic-name">{clinicName}</span></>} and save as you go.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                status === "submitted" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
              }`}
              data-testid="form-status"
            >
              {status === "submitted" ? "Submitted" : "Draft"}
            </span>
            <Button variant="outline" onClick={blitzAutofill} data-testid="blitz-autofill-btn" style={{ borderColor: accent, color: accent }}>
              <Zap className="mr-1.5 h-4 w-4" /> Blitz Autofill
            </Button>
            <Button variant="outline" onClick={downloadPdf} data-testid="download-pdf-btn">
              <FileDown className="mr-1.5 h-4 w-4" /> Download PDF
            </Button>
            <Button onClick={() => save("draft")} disabled={saving} variant="secondary" data-testid="save-draft-btn">
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Save
            </Button>
          </div>
        </div>

        {/* Family Overview — pediatric one-glance status of every child */}
        {hasChildSection && blitzes.length > 0 && (
          <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4" data-testid="family-overview">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ListChecks className="h-4 w-4" style={{ color: accent }} />
                <h2 className="font-semibold text-slate-900">Family overview</h2>
              </div>
              <span className="text-xs font-semibold text-slate-500" data-testid="family-progress-count">
                {blitzes.filter((b) => b.status === "submitted").length} of {blitzes.length} submitted
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {blitzes.map((b) => {
                const done = b.status === "submitted";
                const active = b.form_id === formId;
                return (
                  <button
                    key={b.form_id}
                    onClick={() => switchBlitz(b.form_id)}
                    data-testid={`family-chip-${b.form_id}`}
                    style={active ? { borderColor: accent } : undefined}
                    className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors ${active ? "bg-slate-50" : "border-slate-200 hover:bg-slate-50"}`}
                  >
                    {done
                      ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                      : <Circle className="h-4 w-4 text-amber-500" />}
                    <span className="font-medium text-slate-800">{b.label || "New blitz"}</span>
                    <span className={`text-xs ${done ? "text-green-600" : "text-amber-600"}`}>
                      {done ? "Done" : "In progress"}
                    </span>
                  </button>
                );
              })}
              <button
                onClick={() => { setNewChildName(""); setAddOpen(true); }}
                data-testid="family-add-child-btn"
                className="flex items-center gap-1.5 rounded-full border border-dashed border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-500 transition-colors hover:border-slate-400 hover:text-slate-700"
                style={{ color: accent, borderColor: `${accent}66` }}
              >
                <Plus className="h-4 w-4" /> Add another child
              </button>
            </div>
          </div>
        )}

        {/* Progress */}
        <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4">
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="font-semibold text-slate-700">Overall progress</span>
            <span className="font-semibold" style={{ color: accent }} data-testid="progress-percent">{progress}%</span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
            <motion.div
              className="h-full rounded-full"
              style={{ backgroundColor: accent }}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-12">
          {/* Left sidebar: blitz switcher + step nav */}
          <aside className="lg:col-span-3 space-y-4">
            {(blitzes.length > 0 || hasChildSection) && (
              <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="blitz-switcher">
                <div className="mb-2 flex items-center gap-1.5 px-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {hasChildSection ? <Baby className="h-3.5 w-3.5" /> : <Users className="h-3.5 w-3.5" />}
                  {hasChildSection ? "Children" : "Your forms"}
                </div>
                <div className="flex gap-2 overflow-x-auto lg:flex-col lg:overflow-visible">
                  {blitzes.map((b) => {
                    const active = b.form_id === formId;
                    const label = b.label || (hasChildSection ? "New blitz" : (user?.name || "New blitz"));
                    const initials = (label || "?").trim().slice(0, 1).toUpperCase();
                    return (
                      <div key={b.form_id} className="group relative">
                        <button
                          onClick={() => switchBlitz(b.form_id)}
                          data-testid={`blitz-bubble-${b.form_id}`}
                          style={active ? { borderColor: accent, backgroundColor: `${accent}12` } : undefined}
                          className={`flex w-full min-w-max items-center gap-2.5 rounded-lg border px-2.5 py-2 pr-8 text-left text-sm transition-colors lg:min-w-0 ${active ? "" : "border-transparent hover:bg-slate-50"}`}
                        >
                          <span
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                            style={{ backgroundColor: accent }}
                          >
                            {initials}
                          </span>
                          <span className="flex-1 leading-tight">
                            <span className="block font-medium text-slate-800">{label}</span>
                            <span className={`text-[11px] ${b.status === "submitted" ? "text-green-600" : "text-amber-600"}`}>
                              {b.status === "submitted" ? "Submitted" : "In progress"}
                            </span>
                          </span>
                        </button>
                        {blitzes.length > 1 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(b); }}
                            data-testid={`delete-blitz-${b.form_id}`}
                            title="Delete this form"
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-slate-300 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-500 focus:opacity-100 group-hover:opacity-100"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  <button
                    onClick={() => { setNewChildName(""); setAddOpen(true); }}
                    data-testid="add-blitz-btn"
                    className="flex min-w-max items-center gap-2 rounded-lg border border-dashed border-slate-300 px-2.5 py-2 text-left text-sm font-medium text-slate-500 transition-colors hover:border-slate-400 hover:text-slate-700 lg:min-w-0"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-dashed border-slate-300">
                      <Plus className="h-4 w-4" />
                    </span>
                    {hasChildSection ? "Add another child" : "Add another form"}
                  </button>
                </div>
              </div>
            )}
            <nav className="flex gap-2 overflow-x-auto rounded-xl border border-slate-200 bg-white p-2 lg:flex-col lg:overflow-visible">
              {visibleSteps.map((s, i) => {
                const filled = stepFilledCount(s);
                const total = stepFieldTotal(s);
                const done = filled === total && total > 0;
                const active = i === step;
                return (
                  <button
                    key={s.id}
                    onClick={() => setStep(i)}
                    data-testid={`step-nav-${s.id}`}
                    style={active ? { backgroundColor: accent, color: "#fff" } : undefined}
                    className={`flex min-w-max items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm transition-colors lg:min-w-0 ${
                      active ? "" : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {done ? (
                      <CheckCircle2 className={`h-4 w-4 shrink-0 ${active ? "text-white" : "text-green-500"}`} />
                    ) : (
                      <Circle className={`h-4 w-4 shrink-0 ${active ? "text-white" : "text-slate-300"}`} />
                    )}
                    <span className="flex-1">
                      <span className="block font-medium leading-tight">{s.title}</span>
                      <span className={`hidden text-xs lg:block ${active ? "text-blue-100" : "text-slate-400"}`}>
                        {filled}/{total} fields
                      </span>
                    </span>
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Step content */}
          <main className="lg:col-span-9">
            <motion.div
              key={current.id}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.25 }}
              className="rounded-xl border border-slate-200 bg-white"
            >
              <div className="border-b border-slate-100 bg-slate-50/60 px-6 py-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#0F52BA]">
                  Step {step + 1} of {visibleSteps.length}
                </p>
                <h2 className="mt-0.5 text-xl font-semibold text-slate-900">{current.title}</h2>
                <p className="text-sm text-slate-500">{current.subtitle}</p>
              </div>

              <div className="space-y-6 p-6">
                {current.consent && (
                  <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed text-slate-600">
                    {current.consent.map((p, idx) => (
                      <p key={`${current.id}-consent-${idx}`}>{p}</p>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
                  {current.fields.map((f) => (
                    <div key={f.key} className={f.full || ["checkboxes", "radio", "textarea", "signature", "photo"].includes(f.type) ? "md:col-span-2" : ""}>
                      <FieldRenderer
                        field={f}
                        value={(data[current.key] || {})[f.key]}
                        onChange={(v) => setField(current.key, f.key, v)}
                        invalid={showErrors && f.required && !isFilled((data[current.key] || {})[f.key])}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-slate-100 px-6 py-4">
                <Button
                  variant="ghost"
                  disabled={step === 0}
                  onClick={() => { setStep((s) => Math.max(0, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  data-testid="prev-step-btn"
                >
                  <ChevronLeft className="mr-1 h-4 w-4" /> Previous
                </Button>

                {step < visibleSteps.length - 1 ? (
                  <Button
                    style={{ backgroundColor: accent }}
                    className="text-white hover:opacity-90"
                    onClick={() => { save("draft"); setStep((s) => Math.min(visibleSteps.length - 1, s + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    data-testid="next-step-btn"
                  >
                    Save & Continue <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    className="bg-green-600 hover:bg-green-700"
                    onClick={startEsign}
                    disabled={esignBusy || status === "submitted"}
                    data-testid="submit-form-btn"
                  >
                    {esignBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-1.5 h-4 w-4" />}
                    {status === "submitted" ? "Submitted" : "Sign & Submit"}
                  </Button>
                )}
              </div>
            </motion.div>
          </main>
        </div>
      </div>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o && !deleting) setDeleteTarget(null); }}>
        <DialogContent data-testid="delete-blitz-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" /> Delete this form?
            </DialogTitle>
            <DialogDescription>
              This permanently removes the <strong>{deleteTarget?.label || "New blitz"}</strong> form and all its saved answers. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting} data-testid="delete-cancel-btn">Cancel</Button>
            <Button
              onClick={deleteBlitz}
              disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="delete-confirm-btn"
            >
              {deleting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={(o) => { if (!creating) setAddOpen(o); }}>
        <DialogContent data-testid="add-blitz-dialog">
          <DialogHeader>
            <DialogTitle>{hasChildSection ? "Add another child" : "Add another form"}</DialogTitle>
            <DialogDescription>
              {hasChildSection
                ? "We'll start a fresh blitz for this child and carry over your parent & insurance details."
                : "Start a new, separate form under this account."}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              {hasChildSection ? "Child's first name" : "Name this form (optional)"}
            </label>
            <Input
              value={newChildName}
              onChange={(e) => setNewChildName(e.target.value)}
              placeholder={hasChildSection ? "e.g. Emma" : "e.g. Second application"}
              data-testid="dialog-child-name-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)} disabled={creating} data-testid="dialog-cancel-btn">Cancel</Button>
            <Button
              onClick={() => createBlitz({ label: newChildName.trim(), resetChild: hasChildSection })}
              disabled={creating || (hasChildSection && !newChildName.trim())}
              style={{ backgroundColor: accent }}
              className="text-white hover:opacity-90"
              data-testid="dialog-create-blitz-btn"
            >
              {creating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={esignOpen} onOpenChange={(o) => { if (!o) { stopPolling(); setEsignOpen(false); } }}>
        <DialogContent className="max-w-3xl p-0" data-testid="esign-dialog">
          <DialogHeader className="border-b border-slate-100 px-6 py-4">
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#0F52BA]" /> Sign your paperwork</DialogTitle>
            <DialogDescription>Review the document and sign below. This is a legally-binding e-signature — we'll submit automatically once you're done.</DialogDescription>
          </DialogHeader>
          {esignUrl ? (
            <iframe title="Sign document" src={esignUrl} className="h-[70vh] w-full border-0" data-testid="esign-iframe" />
          ) : (
            <div className="flex h-[40vh] items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#0F52BA]" /></div>
          )}
          <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-6 py-3 text-xs text-slate-400">
            <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Waiting for your signature…</span>
            <span>Powered by eSignatures.io</span>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
